import { DATE_TIME_FORMATS } from '@nandosaus/constants';
import { get, isEmpty, isUndefined, omitBy } from 'lodash';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React from 'react';

import { assetQuery, floodlightQuery, seomaticQuery } from '../../content/query-snippets';
import { cmsImagesPropTypes, floodlightPageLoadEventPropTypes } from '../../prop-types';
import { ContentMatrix } from '../../components/content-matrix';
import { ContentSubHeader, PageHeaderBlock } from '../../components/content-image';
import { extractSeoMetaFromEntry, seoPropDefaults, seoPropTypes } from '../../utils/extract-seo-meta-from-entry';
import { fetch } from '../../content/queries';
import { getFeatureFlags } from '../../utils/feature-flags';
import { Layout } from '../../components/layout';
import { logger } from '../../utils/logger';
import { navigation } from '../../constants';
import { setCacheHeaders } from '../../utils/set-cache-headers';
import { Subheading } from '../../components/typography';
import Error from '../_error';

const ArticlePage = ({ news, metaContent, statusCode, floodlightPageLoadEvent }) => {
  if (statusCode) {
    return <Error statusCode={statusCode} />;
  }

  const { title, pageHeaderImage, date, contentMatrix } = news;
  const formattedDate = date ? dayjs(date).format(DATE_TIME_FORMATS.DATE_DAY_MONTH_FULL) : null;

  const breadcrumbs = {
    title,
    ancestors: [
      {
        title: 'News',
        url: navigation.NEWS,
      },
    ],
  };

  const isHeaderImageEmpty = isEmpty(pageHeaderImage);

  return (
    <Layout title={title} metaContent={metaContent} floodlightPageLoadEvent={floodlightPageLoadEvent}>
      {!isHeaderImageEmpty && <PageHeaderBlock backgroundImage={pageHeaderImage} />}
      <ContentSubHeader {...{ title, breadcrumbs }}>
        {formattedDate && <Subheading>{formattedDate}</Subheading>}
      </ContentSubHeader>
      <ContentMatrix blocks={contentMatrix} />
    </Layout>
  );
};

ArticlePage.getInitialProps = async ({ query, res }) => {
  const preview = get(query, 'x-craft-live-preview');
  const token = get(query, 'token');
  const slug = get(query, 'slug', '');

  const newsQuery = gql`
      query getNewsFromCMS {
        entry(section: "news", slug: "${slug}", siteId: "${process.env.cms.siteId}") {
          ... on news_news_Entry {
            slug
            title
            date
            newsCategory {
              title
            }
            ${assetQuery('pageHeaderImage')}
            ${ContentMatrix.query}
            ${floodlightQuery}
          }
          ${seomaticQuery(`news-and-events/${slug}`)}
        }
      }
    `;
  try {
    const data = await fetch({
      query: newsQuery,
      headers: omitBy({ 'x-craft-live-preview': preview, token }, isUndefined),
    });

    const news = get(data, 'entry', {});
    const floodlightPageLoadEvent = get(news, 'floodlightPageLoadEvent[0]');
    const metaContent = extractSeoMetaFromEntry(news);

    if (res) {
      if (!news) {
        // eslint-disable-next-line no-param-reassign
        res.statusCode = 404;
        return { statusCode: 404 };
      }

      const isPreview = preview && token;
      setCacheHeaders({ res, isPreview });
    }

    const { flags: featureFlags, launchDarklyEnabled } = await getFeatureFlags();
    return { news, metaContent, floodlightPageLoadEvent, featureFlags, launchDarklyEnabled };
  } catch (err) {
    logger.error('CMS: Failed to fetch news from CMS', err);
    return { statusCode: 500 };
  }
};

ArticlePage.propTypes = {
  news: PropTypes.shape({
    contentMatrix: PropTypes.arrayOf(PropTypes.shape({})),
    date: PropTypes.string,
    pageHeaderImage: cmsImagesPropTypes,
    slug: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  floodlightPageLoadEvent: floodlightPageLoadEventPropTypes,
  metaContent: seoPropTypes,
  statusCode: PropTypes.number,
};

ArticlePage.defaultProps = {
  floodlightPageLoadEvent: undefined,
  metaContent: seoPropDefaults,
  statusCode: null,
};

export default ArticlePage;
